import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SwitchToggle from "../../components/SwitchToggle";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import {
  useDeleteCategoryMutation,
  useLazyGetCategoryQuery,
  useUpdateCategoryMutation,
} from "../../service/category";
import { showError, showToast } from "../../constants/toasts";
import { Category } from "../../types/General";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";
import ConfirmationDialog from "../../Modals/confirmationModal";
import usePermission from "../../hooks/usePermission";

const PromotionList = ({ search }: { search?: string }) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { isAdd,isEdit,isDelete } = usePermission({
      label: "Manage Product Categories",
      page_permission: "isView",
    });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [rows, setRows] = useState<Category[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [limit] = useState<number>(10);

  // API Hooks - adjust these based on your actual category service
  const [getCategories, { isLoading }] = useLazyGetCategoryQuery();
  const [updateCategoryStatus, { isLoading: isUpdatingStatus }] =
    useUpdateCategoryMutation();
  const [deleteCategory, { isLoading: isDeleting }] =
    useDeleteCategoryMutation();

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedCategory(null);
  };

  console.log(search, "searchsearchsearch");

  // Handle status toggle
  const handleStatusChange = async (category: Category) => {
    try {
      const response = await updateCategoryStatus({
        id: category._id,
        body: { isBlocked: !category.isBlocked },
      }).unwrap();

      if (response.code === 200) {
        showToast(
          `Category ${
            !category.isBlocked ? "activated" : "deactivated"
          } successfully`
        );
        // Update local state
        setRows((prevRows) =>
          prevRows.map((cat) =>
            cat._id === category._id
              ? { ...cat, isBlocked: !category.isBlocked }
              : cat
          )
        );
      }
    } catch (error: any) {
      showError(error.data?.message || "Failed to update category status");
    }
  };

  // Handle delete category
  const handleDeleteCategory = async () => {
    if (!selectedCategory) return;

    try {
      const response = await deleteCategory({
        id: selectedCategory._id,
      }).unwrap();

      if (response.code === 200) {
        showToast("Category deleted successfully");
        handleCloseDeleteModal();

        // Refresh the list or remove from local state
        if (rows.length === 1 && page > 1) {
          // If it's the last item on the page, go to previous page
          setPage((prev) => prev - 1);
          getCategoriesList(page - 1);
        } else {
          getCategoriesList(page);
        }
      }
    } catch (error: any) {
      showError(error.data?.message || "Failed to delete category");
    }
  };

  // Fetch categories
  const getCategoriesList = async (currentPage?: number) => {
    try {
      const response = await getCategories({
        page: currentPage || page,
        limit,
        ...(search && { search }),
      }).unwrap();

      if (response.code === 200) {
        setRows(response?.data?.list || []);
        setTotalCount(response?.data?.totalCount || 0);
      }
    } catch (error: any) {
      showError(error.data?.message || "Failed to fetch categories");
      setRows([]);
      setTotalCount(0);
    }
  };

  // Open delete confirmation modal
  const handleOpenDeleteModal = (category: Category) => {
    setSelectedCategory(category);
    setDeleteModalOpen(true);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    getCategoriesList(newPage);
  };

  useEffect(() => {
    getCategoriesList();
  }, [search]);

  const totalPages = Math.ceil(totalCount / limit);

  const loading = isLoading || isUpdatingStatus || isDeleting;

  return (
    <>
      <TableContainer className="table_container">
        <Loader isLoad={loading} />
        <Table>
          <TableHead>
            <TableRow>
              {/* <TableCell>Sort</TableCell> */}
              <TableCell>Title</TableCell>
              <TableCell>Service Fee</TableCell>
              <TableCell>Featured</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.length > 0 ? (
              rows?.map((promo) => (
                <TableRow hover key={promo._id}>
                  {/* <TableCell>
                <DragHandleIcon className="drag_handle" />
              </TableCell> */}
                  <TableCell>
                    <Box
                      component="a"
                      className="user_block click_action"
                      onClick={() => navigate("/manage-categories/detail")}
                    >
                      {/* <figure className="no_radius">
                    <img src={promo.banner} alt={promo.title} />
                  </figure> */}
                      <p>
                        <b>{promo.name || ""}</b>
                      </p>
                    </Box>
                  </TableCell>
                  <TableCell>{promo.serviceFee}</TableCell>
                  <TableCell>{promo.isFeatured ? "Featured" : "-"}</TableCell>
                  <TableCell>
                    {" "}
                    <Tooltip
                      title={promo.description || ""}
                      placement="top"
                      arrow
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 250,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                        }}
                      >
                        {promo.description?.length > 50
                          ? promo.description.substring(0, 50) + "..."
                          : promo.description}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <SwitchToggle
                    disabled={!isEdit}
                      onChange={() => {
                        handleStatusChange(promo);
                      }}
                      checked={!promo?.isBlocked}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="table_actions v2">
                      <IconButton
                        onClick={() =>
                          navigate(`/manage-categories/detail/${promo?._id}`)
                        }
                      >
                        <img src="/static/images/eye_icon.svg" alt="View" />
                      </IconButton>
                    {isEdit &&  <IconButton
                        onClick={() =>
                          navigate(`/manage-categories/edit/${promo?._id}`)
                        }
                      >
                        <img src="/static/images/edit2_icon.svg" alt="Edit" />
                      </IconButton>}
                   {isDelete &&   <IconButton>
                        <img
                          src="/static/images/trash2_icon.svg"
                          alt="Delete"
                          onClick={() => {
                            handleOpenDeleteModal(promo);
                          }}
                        />
                      </IconButton>}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow hover>
                <TableCell colSpan={6}>
                  <Box textAlign={"center"}>No data found</Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalCount > 0 && (
        // <Box mt={3} display="flex" justifyContent="center">
        <Pagination
          module={rows}
          page={page}
          onPageChange={handlePageChange}
          totalPages={totalPages}
          setPage={setPage}
          totalCount={totalCount}
        />

        // </Box>
      )}
      <ConfirmationDialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteCategory}
        title="Delete User"
        confirmText="Delete"
        cancelText="Cancel"
        message={`Are you sure you want to delete category "${selectedCategory?.name || ""} "?`}
        isLoading={isDeleting}
      />
    </>
  );
};

export default PromotionList;
